import React, { useState } from 'react';
import {
	LaboratoryRequestCreateDocumentInfo,
	LaboratoryRequestCreatePatient,
} from './LaboratoryRequestCreateRequestHeaderForm';
import { LaboratoryRequestFormDataScheme } from '../LaboratoryRequestCreate/LaboratoryRequestCreateForm';
// @ts-ignore
import styles from './LaboratoryRequestCreateRecapitulation.module.scss';
import Text from '../../../../Components/Base/Text/Text';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import usePatientConsent from '../../../../Components/PatientProfile/Hooks/usePatientConsent';
import FormControl from '../../../../Components/Form/FormControl';
import Checkbox from '../../../../Components/Base/Checkbox/Checkbox';
import PatientNoConsentProvided from '../../../Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';

interface Props {
	onConfirm: (saveToECard: boolean) => void;
	patientId: string;
	data: {
		patient?: LaboratoryRequestCreatePatient;
		documentInfo?: LaboratoryRequestCreateDocumentInfo;
		data?: LaboratoryRequestFormDataScheme[];
	};
}

const LaboratoryRequestCreateRecapitulation: React.FC<Props> = ({
	onConfirm,
	data,
	patientId,
}) => {
	const patient = data?.patient;
	const documentInfo = data?.documentInfo;
	const requestData = data?.data;
	const { isConsentProvided } = usePatientConsent(patientId);
	const [saveToECard, setSaveToECard] = useState(true);

	return (
		<div className={styles.wrapper}>
			<div className='row'>
				<div className='col-6'>
					{requestData && (
						<div className={styles.group}>
							<div className={styles.item}>
								<Text
									className={styles.title}
									tx='component.laboratoryRequestCreateRecapitulation.requests'
									colon
									firstUpper
								/>
							</div>
							{requestData?.map(item => (
								<div className={styles.item}>{item.note}</div>
							))}
						</div>
					)}
				</div>
				<div className='col-6'>
					{patient && (
						<div className={styles.group}>
							<div className={styles.item}>
								<Text
									tx='component.laboratoryRequestCreateRecapitulation.patient'
									className={styles.label}
									colon
									firstUpper
								/>
								<div>
									{patient.firstName} {patient.lastName}
								</div>
							</div>
							<div className={styles.item}>
								<Text
									tx='component.laboratoryRequestCreateRecapitulation.insuranceNumber'
									className={styles.label}
									colon
									firstUpper
								/>
								<div>{patient.insuranceNumber}</div>
							</div>
							<div className={styles.item}>
								<Text
									tx='component.laboratoryRequestCreateRecapitulation.birthDate'
									className={styles.label}
									colon
									firstUpper
								/>
								<FormattedMomentDate
									date={patient.birthDate}
									format={DateFormat.DATE}
								/>
							</div>
							<div className={styles.item}>
								<Text
									tx='component.laboratoryRequestCreateRecapitulation.address'
									className={styles.label}
									colon
									firstUpper
								/>
								<div>
									{patient.address.street} {patient.address.descriptiveNumber}{' '}
									{patient.address.guideNumber
										? `/${patient.address.guideNumber}`
										: ''}
									{', '}
									{patient.address.city} {patient.address.postCode}
								</div>
							</div>
							<div className={styles.item}>
								<Text
									tx='component.laboratoryRequestCreateRecapitulation.insuranceCompany'
									className={styles.label}
									colon
									firstUpper
								/>
								{patient.insuranceCompany.label}
							</div>
							<div className={styles.item}>
								<Text
									tx='component.laboratoryRequestCreateRecapitulation.gender'
									className={styles.label}
									colon
									firstUpper
								/>
								{patient.gender.label}
							</div>
							<div className={styles.item}>
								<Text
									tx='component.laboratoryRequestCreateRecapitulation.diagnosis'
									className={styles.label}
									colon
									firstUpper
								/>
								{patient.diagnosis.label}
							</div>
						</div>
					)}
					{documentInfo && (
						<div className={styles.group}>
							{documentInfo.tookBy?.label && (
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.tookBy'
										className={styles.label}
										colon
										firstUpper
									/>
									<div>{documentInfo.tookBy.label}</div>
								</div>
							)}
							{documentInfo.tookByCustom && (
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.tookBy'
										className={styles.label}
										colon
										firstUpper
									/>
									<div>{documentInfo.tookByCustom}</div>
								</div>
							)}
							{documentInfo.date && (
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.date'
										className={styles.label}
										colon
										firstUpper
									/>
									<FormattedMomentDate
										date={documentInfo.date}
										format={DateFormat.DATE}
									/>
								</div>
							)}
							{documentInfo.time && (
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.time'
										className={styles.label}
										colon
										firstUpper
									/>
									<FormattedMomentDate
										date={documentInfo.time}
										format={DateFormat.TIME}
									/>
								</div>
							)}
							<div className={styles.item}>
								<Text
									tx='component.laboratoryRequestCreateRecapitulation.primarySampleType'
									className={styles.label}
									colon
									firstUpper
								/>
								{documentInfo.primarySampleType}
							</div>
							<div className={styles.item}>
								<Text
									tx='component.laboratoryRequestCreateRecapitulation.statim'
									className={styles.label}
									colon
									firstUpper
								/>
								<Text
									tx={documentInfo.statim ? 'base.yes' : 'base.no'}
									inline
									firstUpper
								/>
							</div>
							{documentInfo.statimPhoneNumber && (
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.statimPhoneNumber'
										className={styles.label}
										colon
										firstUpper
									/>
									{documentInfo.statimPhoneNumber}
								</div>
							)}
							<div className={styles.item}>
								<Text
									tx='component.laboratoryRequestCreateRecapitulation.selfPaid'
									className={styles.label}
									colon
									firstUpper
								/>
								<Text
									tx={documentInfo.selfPaid ? 'base.yes' : 'base.no'}
									inline
									firstUpper
								/>
							</div>
						</div>
					)}
				</div>
			</div>
			<div>
				{isConsentProvided ? (
					<Checkbox
						tx={'component.laboratoryRequestCreateRecapitulation.saveToECard'}
						value={saveToECard}
						onChange={() => setSaveToECard(!saveToECard)}
					/>
				) : (
					<PatientNoConsentProvided patientId={patientId} />
				)}
			</div>
			<div className='mt-3 d-flex'>
				<Button
					theme={Theme.GREEN}
					onClick={() => onConfirm(saveToECard)}
					title='base.create'
				/>
			</div>
		</div>
	);
};

export default LaboratoryRequestCreateRecapitulation;
